import React, { useState } from 'react';
import { Plus, LayoutDashboard, LogOut, Download, Copy, Check, Wifi, WifiOff, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ProjectTable } from '../components/admin/ProjectTable';
import { ProjectForm } from '../components/admin/ProjectForm';
import { HeroImagesSection } from '../components/admin/HeroImagesSection';
import { ClientLogosSection } from '../components/admin/ClientLogosSection';
import { BulkImport } from '../components/admin/BulkImport';
import { Modal } from '../components/ui/Modal';
import { Button } from '../components/ui/Button';
import { useProjects, ProjectData } from '../hooks/useProjects';
import { useHeroImages } from '../hooks/useHeroImages';
import { useClientLogos } from '../hooks/useClientLogos';
export function AdminPage() {
  const {
    projects,
    loading: projectsLoading,
    error: projectsError,
    addProject,
    updateProject,
    deleteProject,
    toggleFeatured,
    reloadProjects
  } = useProjects();
  const {
    images: heroImages,
    loading: imagesLoading,
    error: imagesError
  } = useHeroImages();
  const {
    logos: clientLogos,
    loading: logosLoading,
    error: logosError
  } = useClientLogos();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<ProjectData | null>(null);
  const [copied, setCopied] = useState(false);
  const hasError = projectsError || imagesError || logosError;
  const isConnected = !hasError;
  const isLoading = projectsLoading || imagesLoading || logosLoading;
  const handleCreate = () => {
    setEditingProject(null);
    setIsModalOpen(true);
  };
  const handleEdit = (project: ProjectData) => {
    setEditingProject(project);
    setIsModalOpen(true);
  };
  const handleDelete = (id: string) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este proyecto?')) {
      deleteProject(id);
    }
  };
  const handleSubmit = (data: ProjectData) => {
    if (editingProject && editingProject.id) {
      updateProject(editingProject.id, data);
    } else {
      addProject(data);
    }
    setIsModalOpen(false);
  };
  const handleBulkImport = async (importedProjects: ProjectData[]) => {
    for (const project of importedProjects) {
      await addProject(project);
    }
    await reloadProjects();
  };
  const handleExport = () => {
    setIsExportModalOpen(true);
  };
  const exportData = JSON.stringify({
    projects,
    heroImages,
    clientLogos
  }, null, 2);
  const handleCopy = () => {
    navigator.clipboard.writeText(exportData);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <div className="min-h-screen bg-gray-50 font-sans text-gray-900" data-id="element-587">
      {/* Top Navigation */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm" data-id="element-588">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-589">
          <div className="flex justify-between items-center h-16" data-id="element-590">
            <div className="flex items-center gap-3" data-id="element-591">
              <div className="bg-blue-600 p-2 rounded-lg" data-id="element-592">
                <LayoutDashboard className="h-5 w-5 text-white" data-id="element-593" />
              </div>
              <h1 className="text-xl font-bold text-gray-900" data-id="element-594">
                Panel de Administración
              </h1>

              {/* Firebase Connection Status */}
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${isConnected ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-id="element-595">
                {isConnected ? <>
                    <Wifi className="h-3 w-3" data-id="element-596" />
                    <span data-id="element-597">Conectado a Firebase</span>
                  </> : <>
                    <WifiOff className="h-3 w-3" data-id="element-598" />
                    <span data-id="element-599">Error de conexión</span>
                  </>}
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-600">
              <Button onClick={handleExport} variant="secondary" leftIcon={<Download className="h-4 w-4" data-id="element-602" />} className="border-green-600 text-green-600 hover:bg-green-50" disabled={hasError} data-id="element-601">
                Exportar Datos
              </Button>
              <div className="h-4 w-px bg-gray-300" data-id="element-603" />
              <Link to="/" className="text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors" data-id="element-604">
                Ver Sitio Web
              </Link>
              <div className="h-4 w-px bg-gray-300" data-id="element-605" />
              <button className="flex items-center gap-2 text-sm font-medium text-red-600 hover:text-red-700 transition-colors" data-id="element-606">
                <LogOut className="h-4 w-4" data-id="element-607" />
                <span data-id="element-608">Salir</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-609">
        {/* Firebase Error Banner */}
        {hasError && <div className="mb-8 bg-red-50 border-2 border-red-200 rounded-xl p-6" data-id="element-610">
            <div className="flex items-start gap-4" data-id="element-611">
              <AlertCircle className="h-6 w-6 text-red-600 mt-0.5 flex-shrink-0" data-id="element-612" />
              <div className="flex-1" data-id="element-613">
                <p className="text-lg text-red-900 font-bold mb-3" data-id="element-614">
                  ⚠️ Error de conexión con Firebase
                </p>
                <p className="text-sm text-red-800 mb-4 font-medium" data-id="element-615">
                  {projectsError || imagesError || logosError}
                </p>

                <div className="bg-white rounded-lg p-4 border border-red-200" data-id="element-616">
                  <p className="text-sm font-bold text-gray-900 mb-3" data-id="element-617">
                    🔧 Solución paso a paso:
                  </p>
                  <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside" data-id="element-618">
                    <li data-id="element-619">
                      Ve a{' '}
                      <a href="https://console.firebase.google.com/project/forax-web/firestore" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium" data-id="element-620">
                        Firebase Console → Firestore Database
                      </a>
                    </li>
                    <li data-id="element-621">
                      Si no está creada, haz clic en{' '}
                      <strong data-id="element-622">"Create database"</strong>
                    </li>
                    <li data-id="element-623">
                      Selecciona <strong data-id="element-624">"Start in test mode"</strong>
                    </li>
                    <li data-id="element-625">
                      Elige región <strong data-id="element-626">"us-central1"</strong>
                    </li>
                    <li data-id="element-627">
                      Haz clic en <strong data-id="element-628">"Enable"</strong>
                    </li>
                    <li data-id="element-629">Espera 1-2 minutos y recarga esta página</li>
                  </ol>
                </div>

                <div className="mt-4" data-id="element-630">
                  <Button onClick={() => window.location.reload()} className="bg-red-600 hover:bg-red-700" data-id="element-631">
                    Reintentar conexión
                  </Button>
                </div>
              </div>
            </div>
          </div>}

        {/* Loading State */}
        {isLoading && !hasError && <div className="mb-8 bg-blue-50 border border-blue-200 rounded-xl p-8 text-center" data-id="element-632">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent mb-4" data-id="element-633"></div>
            <p className="text-sm text-blue-900 font-semibold" data-id="element-634">
              Cargando datos desde Firebase...
            </p>
            <p className="text-xs text-blue-700 mt-2" data-id="element-635">
              Si esto tarda más de 10 segundos, verifica que Firestore esté
              activado
            </p>
          </div>}

        {/* Success State */}
        {!isLoading && !hasError && <>
            <div className="mb-8 bg-green-50 border border-green-200 rounded-xl p-4" data-id="element-636">
              <div className="flex items-start gap-3" data-id="element-637">
                <Wifi className="h-5 w-5 text-green-600 mt-0.5" data-id="element-638" />
                <div className="flex-1" data-id="element-639">
                  <p className="text-sm text-green-900 font-semibold mb-2" data-id="element-640">
                    ✅ Conectado a Firebase - Administración en la nube activa
                  </p>
                  <p className="text-sm text-green-800" data-id="element-641">
                    Todos tus cambios se sincronizan automáticamente en todos
                    los dispositivos. Puedes editar desde cualquier lugar y los
                    cambios se verán instantáneamente.
                  </p>
                </div>
              </div>
            </div>

            {/* Reminder Banner */}
            <div className="mb-8 bg-blue-50 border-2 border-blue-200 rounded-xl p-4" data-id="element-642">
              <div className="flex items-start gap-3" data-id="element-643">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm" data-id="element-644">
                  💡
                </div>
                <div className="flex-1" data-id="element-645">
                  <p className="text-sm font-bold text-blue-900 mb-2" data-id="element-646">
                    📍 Recordatorio: ¿Dónde hago los cambios?
                  </p>
                  <div className="space-y-2 text-sm text-blue-800" data-id="element-647">
                    <div className="flex items-start gap-2" data-id="element-648">
                      <span className="font-bold text-blue-600 mt-0.5" data-id="element-649">✓</span>
                      <div data-id="element-650">
                        <strong data-id="element-651">AQUÍ (Panel Admin):</strong> Gestiona contenido
                        (proyectos, imágenes del hero, logos de clientes). Los
                        cambios se sincronizan automáticamente con Firebase.
                      </div>
                    </div>
                    <div className="flex items-start gap-2" data-id="element-652">
                      <span className="font-bold text-purple-600 mt-0.5" data-id="element-653">
                        ✓
                      </span>
                      <div data-id="element-654">
                        <strong data-id="element-655">Magic Patterns:</strong> Modifica diseño,
                        colores, textos fijos, estructura de componentes. Luego
                        haz "Publish" para aplicar cambios.
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-blue-200" data-id="element-656">
                    <p className="text-xs text-blue-700" data-id="element-657">
                      <strong data-id="element-658">Regla simple:</strong> Contenido dinámico = Panel
                      Admin | Diseño y estructura = Magic Patterns
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bulk Import Section */}
            <div className="mb-8" data-id="element-659">
              <BulkImport onImport={handleBulkImport} data-id="element-660" />
            </div>

            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8" data-id="element-661">
              <div data-id="element-662">
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-663">Proyectos</h2>
                <p className="mt-1 text-gray-500" data-id="element-664">
                  Gestiona el portafolio de proyectos y sus fotografías.
                </p>
              </div>
              <Button onClick={handleCreate} leftIcon={<Plus className="h-4 w-4" data-id="element-666" />} data-id="element-665">
                Nuevo Proyecto
              </Button>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-667">
              <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-668">
                <p className="text-sm font-medium text-gray-500" data-id="element-669">
                  Total Proyectos
                </p>
                <p className="text-3xl font-bold text-gray-900 mt-2" data-id="element-670">
                  {projects.length}
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-671">
                <p className="text-sm font-medium text-gray-500" data-id="element-672">
                  Categorías Activas
                </p>
                <p className="text-3xl font-bold text-gray-900 mt-2" data-id="element-673">
                  {new Set(projects.map(p => p.category)).size}
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-674">
                <p className="text-sm font-medium text-gray-500" data-id="element-675">Destacados</p>
                <p className="text-3xl font-bold text-gray-900 mt-2" data-id="element-676">
                  {projects.filter(p => p.featured).length}
                  <span className="text-lg text-gray-400 font-normal" data-id="element-677">/3</span>
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-678">
                <p className="text-sm font-medium text-gray-500" data-id="element-679">
                  Logos Clientes
                </p>
                <p className="text-3xl font-bold text-gray-900 mt-2" data-id="element-680">
                  {clientLogos.length}
                </p>
              </div>
            </div>

            {/* Projects Management */}
            <div className="mb-6" data-id="element-681">
              <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-682">
                Listado de Proyectos
              </h2>
              <p className="text-gray-500 text-sm" data-id="element-683">
                Administra el contenido del portafolio. Destaca hasta 3
                proyectos para el home.
              </p>
            </div>
            <ProjectTable projects={projects} onEdit={handleEdit} onDelete={handleDelete} onToggleFeatured={toggleFeatured} data-id="element-684" />

            <div className="my-12 border-t border-gray-200" data-id="element-685" />

            {/* Hero Images Management */}
            <HeroImagesSection data-id="element-686" />

            <div className="my-12 border-t border-gray-200" data-id="element-687" />

            {/* Client Logos Management */}
            <ClientLogosSection data-id="element-688" />
          </>}
      </main>

      {/* Edit/Create Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingProject ? 'Editar Proyecto' : 'Nuevo Proyecto'} data-id="element-689">
        <ProjectForm initialData={editingProject} onSubmit={handleSubmit} onCancel={() => setIsModalOpen(false)} data-id="element-690" />
      </Modal>

      {/* Export Modal */}
      <Modal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} title="Exportar Datos (Backup)" data-id="element-691">
        <div className="space-y-4" data-id="element-692">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-693">
            <p className="text-sm text-blue-900 font-semibold mb-2" data-id="element-694">
              📤 Backup de datos
            </p>
            <p className="text-sm text-blue-800" data-id="element-695">
              Con Firebase, tus datos ya están en la nube y sincronizados. Esta
              exportación es solo para backup o migración.
            </p>
          </div>

          <div className="relative" data-id="element-696">
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-auto max-h-96 text-xs font-mono" data-id="element-697">
              {exportData}
            </pre>
          </div>

          <div className="flex justify-end gap-3" data-id="element-698">
            <Button variant="secondary" onClick={() => setIsExportModalOpen(false)} data-id="element-699">
              Cerrar
            </Button>
            <Button onClick={handleCopy} leftIcon={copied ? <Check className="h-4 w-4" data-id="element-701" /> : <Copy className="h-4 w-4" data-id="element-702" />} className={copied ? 'bg-green-600 hover:bg-green-700' : ''} data-id="element-700">
              {copied ? '¡Copiado!' : 'Copiar Datos'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}